<template>
    <edit-resource
        :form="form"
        :title="title"
        @close="$emit('close')"
        @submit="saveUser"
    >
        <div class="w-600">

            <simple-input v-if="emailEditable" v-model="form.email" class="mb-16">
                {{ $t('users.fields.email') }}
            </simple-input>

            <div v-else>
                <label class="mb-8 text-body-sm flex items-center">
                    <span class="first-letter:uppercase">
                        {{ $t('users.fields.email') }}:
                    </span>
                    <span class="pl-4 font-semibold first-letter:uppercase">
                        {{ this.user.email }}
                    </span>
                </label>
            </div>

            <simple-input v-model="form.first_name" class="mb-16" :disabled="currentCompany.uses_sso && user">
                {{ $t('auth.first_name') }}
            </simple-input>

            <simple-input v-model="form.last_name" class="mb-16" :disabled="currentCompany.uses_sso && user">
                {{ $t('auth.last_name') }}
            </simple-input>

            <banner
                v-if="currentCompany.uses_sso && user"
                :description="$t('auth.sso_user_cannot_change_details')"
                class="mt-8"
                inline
            />

            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs" class="w-full">
                <div v-for="lang in languages" :key="lang">
                    <simple-input v-show="lang === selectedLanguage"
                                  v-model="form.position[lang]"
                                  class="mb-16">
                        {{ $t('settings.users.position') }} <span v-if="languages.length > 1">({{ lang }})</span>
                    </simple-input>
                </div>
            </translatable>

            <simple-input v-model="form.phone" class="mb-16">
                {{ $t('settings.users.phone') }}
            </simple-input>

            <select-dropdown
                v-model="form.role_id"
                :auto-translate="false"
                :options="availableRoles"
                class="mb-16"
            >
                {{ $t('users.fields.role') }}
            </select-dropdown>
        </div>
    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Form from '../../etc/forms/Form';
import * as users from '../../services/UserService';
import * as companyUsers from '../../services/CompanyUserService';
import User from '../../models/User';
import Role from '../../models/Role';
import * as roles from '../../services/RoleService';
import {normalizeJson} from '../../../shared/helpers';
import SelectDropdown from '../forms/SelectDropdown.vue';
import Toggle from '../forms/Toggle.vue';
import Translatable from '../forms/Translatable';
import Checkbox from '../forms/Checkbox';
import Banner from "./Banner.vue";
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';
import {useRepo} from "pinia-orm";

function getFields(addEmail = true) {
    return addEmail ? [
        {name: 'first_name', value: null, rules: ['required']},
        {name: 'last_name', value: null, rules: ['required']},
        {name: 'email', value: null, rules: ['required']},
        {name: 'position', value: {}},
        {name: 'phone', value: null},
        {name: 'role_id', value: null},
    ] : [
        {name: 'first_name', value: null, rules: ['required']},
        {name: 'last_name', value: null, rules: ['required']},
        {name: 'position', value: {}},
        {name: 'phone', value: null},
        {name: 'role_id', value: null},
    ];
}

export default {
    components: {
        Banner,
        Checkbox,
        Translatable,
        'edit-resource': EditResource,
        'simple-input': Input,
        'toggle': Toggle,
        'select-dropdown': SelectDropdown,
    },
    props: {
        user: Object,
        currentCompany: Object,
        allUsers: Array,
    },
    data: function () {
        return {
            form: this.generateForm(),
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.position[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        title() {
            return this.$t(this.user ? 'general.edit_subject' : 'general.add_subject', {subject: this.$t('settings.users.user')});
        },
        availableRoles() {

            let options = useRepo(Role)
                .where('company_id', this.currentCompany.id)
                .orderBy(role => {
                    switch (role.locked_name) {
                        case 'company_admin':
                            return 0;
                        case 'member':
                            return 1;
                        case 'marketeer':
                            return 2;
                        case 'recruiter':
                            return 3;
                        case 'accountant':
                            return 4;
                        default:
                            return role.name;
                    }
                })
                .get().map(role => ({
                    value: role.id,
                    label: role.name,
                }));

            if (this.form.role_id === null || !options.some(option => option.value == this.form.role_id)) {
                this.form.role_id = options[0]?.value;
            }

            return options;
        },
        emailEditable() {
            return !this.user ? true : (this.user.id === null ? true : false);
        },
    },
    methods: {
        generateForm() {
            let fields = [];

            if (this.user) {
                const companyUser = this.user.companyUsers.find(cu => cu.company_id === this.currentCompany.id);
                fields = getFields(false);

                fields.forEach((field, index) => {
                    if (['position'].includes(field.name)) {
                        fields[index].value = JSON.parse(JSON.stringify(companyUser[field.name] || {}));
                    } else if (!['email', 'first_name', 'last_name'].includes(field.name)) {
                        fields[index].value = JSON.parse(JSON.stringify(companyUser[field.name] || null));
                    } else {
                        fields[index].value = JSON.parse(JSON.stringify(this.user[field.name]));
                    }
                });
            } else {
                fields = getFields();
            }

            return new Form(fields);
        },
        async saveUser() {
            if (this.user && this.user.id) {
                await this.updateUser();
            } else {
                await this.inviteUser();
            }

            this.$emit('close');
        },
        updateUser() {
            const companyUserId = this.user.companyUsers.find(cu => cu.company_id === this.currentCompany.id).id;
            return this.form.submit(data => companyUsers.update(companyUserId, {
                data: {
                    attributes: {
                        phone: data.phone,
                        position: data.position,
                        first_name:  data.first_name,
                        last_name: data.last_name,
                    },
                    relationships: {
                        role: {data: {type: 'roles', id: data.role_id}},
                    },
                },
            }))
                .then(() => users.show(this.user.id, {include: 'userPermissions,companyUsers.role.permissions'}))
                .then(result => {
                    useRepo(User).save(normalizeJson(result.data));
                });
        },
        inviteUser() {
            return this.findUserIdByEmail(this.form.data.email).then(result => {
                if (result) {
                    return this.form.submit(data => companyUsers.store({
                        data: {
                            attributes: {
                                first_name: data.first_name,
                                last_name: data.last_name,
                                phone: data.phone,
                                position: data.position,
                                // default_for_external: data.default_for_external
                            },
                            relationships: {
                                user: {data: {type: 'users', id: result}},
                                role: {data: {type: 'roles', id: data.role_id}},
                                company: {data: {type: 'company', id: this.currentCompany.id}},
                            },
                        },
                    })).then(result => result.attributes.user_id);
                } else {
                    return this.form.submit(data => users.store({
                        data: {
                            attributes: {
                                first_name: data.first_name,
                                last_name: data.last_name,
                                email: data.email,
                            },
                            relationships: {
                                role: {
                                    data: {
                                        id: data.role_id,
                                        pivot: {
                                            phone: data.phone,
                                            position: data.position,
                                            // default_for_external: data.default_for_external
                                        },
                                    },
                                },
                            },
                        },
                    })).then(result => result.id);
                }
            })
                .then(() => users.index())
                .then(result => {
                    useRepo(User).save(normalizeJson(result.data));
                });
        },
        findUserIdByEmail(email) {
            return users.findForInvite(email).then(result => result.id).catch(error => false);
        },
        getDefaultLang() {
            const userLang = this.locale;

            if (this.languages.includes(userLang)) {
                return userLang;
            }

            return this.languages[0];
        },
    },
    beforeCreate() {
        return roles.index().then(result => {
            return useRepo(Role).save(normalizeJson(result.data));
        });
    },
};
</script>
